// Showing a branch to a person.
//
// One rule, in one place, because it is a rule about noise rather than about
// data and every screen has to apply it the same way.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY MOST BRANCHES MUST NOT BE NAMED
//
// Migration 015 gave every existing store a first branch named after the store
// itself. That is the right default — a single-shop business has one place and
// it is called what the business is called — but it means a chip drawn without
// thinking says "TEST · TEST" on 354 rows out of 354. The branch is then pure
// decoration, and worse, it buries the handful of rows where the branch is
// genuinely telling you something.
//
// So: a branch is worth naming when it does not merely repeat the store's name,
// or when the store has more than one branch (at which point even a same-named
// branch is a real choice between places).
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT THIS IS NOT
//
// Not a filter. A branch ATTRIBUTES a transaction, it does not isolate one:
// transaction lists stay store-wide so a booking taken at one branch can be
// paid at another, and a loan raised at one can be received at another. The
// only thing that filters by branch is a report. See utils/branchScope.js on
// the backend — there is a test there that fails if anyone adds a branch filter
// to the pawn loan lookup.
// ─────────────────────────────────────────────────────────────────────────────

// The "every branch" selection. A view mode, not a place: the session is always
// in exactly one branch (or in none, for a service role), and this only widens
// what is being LOOKED at. Matches the string the backend's branchScope reads.
export const ALL_BRANCHES = "all";

const clean = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

/**
 * Does this branch name tell you anything the store name does not?
 *
 * Not just an equality test, because branch_master.name is varchar(25) and
 * migration 015 seeded it from the store name — so every store called more than
 * 25 characters got a TRUNCATED copy of its own name as its branch. On this
 * data that is 76 stores against 258 exact matches, and all 334 single-branch
 * stores fall into one bucket or the other. An equality test alone would let
 * three quarters of them through and print a chip reading
 * "CITY GOLD FASHION JEWELLE", which is noise that also looks like a bug.
 *
 * So a branch name that is a leading prefix of its store's name says nothing
 * new. That deliberately also suppresses a genuine branch called "Main" on a
 * store called "Main Street Jewellers" — which is the right answer anyway,
 * because that only happens on a single-branch store, where naming the one
 * place a thing could have happened tells the reader nothing.
 */
const saysNothingNew = (branch, store) => {
  const b = clean(branch).toLowerCase();
  const s = clean(store).toLowerCase();
  if (!b || !s) return false;
  return s === b || s.startsWith(b);
};

/**
 * The branch name to show, or null to show nothing.
 *
 *   branchLabelFor({ branchName: "Hubli", storeName: "KGP", branchCount: 2 })
 *   // → "Hubli"
 *   branchLabelFor({ branchName: "KGP", storeName: "KGP" })
 *   // → null   (says nothing twice)
 *
 * `branchCount` is optional and unknown counts as one. That is the safe
 * default: a cross-store list (Kumudu's queue) knows each row's store and
 * branch name but not how many branches that store has, and guessing "many"
 * there would bring back the 354-rows-of-noise this rule exists to prevent.
 */
export const branchLabelFor = ({ branchName, storeName, branchCount } = {}) => {
  const branch = clean(branchName);
  if (!branch) return null;

  // More than one branch to choose between: the name is worth printing even
  // when it repeats the store's, because now it is separating two real places.
  const count = Number(branchCount ?? 0);
  if (count > 1) return branch;

  return saysNothingNew(branch, storeName) ? null : branch;
};

/**
 * True when a branch is worth drawing at all — the same test, for callers that
 * want to decide layout before they have a label.
 */
export const shouldShowBranch = (args) => branchLabelFor(args) !== null;

/**
 * "KGP JEWELLERS · Hubli", or just "KGP JEWELLERS".
 *
 * The header/sidebar form. The branch is appended rather than given a line of
 * its own so it can never push anything off a phone screen.
 */
export const storeBranchLabel = ({ storeName, branchName, branchCount } = {}) =>
  [clean(storeName), branchLabelFor({ branchName, storeName, branchCount })]
    .filter(Boolean)
    .join(" · ");
